import { Box, useToken } from "@chakra-ui/react";

import { RANA_POPUP_PADDING_PX } from "../rana-popup/rana-popup";

import { RanaMapButtons } from "./components/rana-map-buttons";
import { RanaMapControls } from "./components/rana-map-controls";
import RanaMapLayers from "./components/rana-map-layers";
import { RanaMapScale } from "./components/rana-map-scale";
import { RanaMapSelf } from "./components/rana-map-self";
import { useFullscreen } from "./hooks/use-fullscreen";
import { useRanaMap } from "./hooks/use-rana-map";
import { RanaMapProps } from "./types/rana-map-props-types";

const { ControlsTopRight, ControlsBottomRight, ...ExportRanaMapControls } = RanaMapControls;

function RanaMap(props: RanaMapProps) {
  const {
    mapId,
    hideExport,
    containerProps,
    commentProps,
    sprites,
    isSmallControls,
    initialFullscreen,
  } = props;
  const { mapContainerRef, fullscreen, setFullscreen } = useFullscreen(initialFullscreen);
  const hookReturn = useRanaMap(mapId, sprites);
  const {
    isThreeD,
    setThreeD,

    zoomIn,
    zoomOut,
    toFlat,
    show3DBuildings,
    setShow3DBuildings,
  } = hookReturn;

  const showSmallControls = !isSmallControls || fullscreen;

  const [borderColor] = useToken("colors", ["brand.border"]);
  const [borderRadius] = useToken("radii", ["base"]);

  return (
    <Box
      h="full"
      w="full"
      position="relative"
      ref={mapContainerRef}
      sx={{
        // MapLibre sets overflow:hidden on .maplibregl-map which clips popups that extend
        // beyond the map boundary. Allow overflow there but keep the canvas itself clipped.
        ".maplibregl-map": { overflow: "visible" },
        ".maplibregl-canvas-container": { overflow: "hidden" },
        // Popup content styles must be applied here because react-map-gl mounts the popup
        // DOM directly inside the map container, not inside its JSX position in the tree.
        ".maplibregl-popup-content": {
          padding: `${RANA_POPUP_PADDING_PX}px`,

          border: `1px solid ${borderColor}`,
          borderRadius,
        },
      }}
      {...containerProps}
    >
      {/* Sidebar */}
      {showSmallControls && (
        <ControlsTopRight overflow="visible">
          {commentProps && (
            <RanaMapButtons.AddComment
              canComment={commentProps.canComment}
              isAddingComment={commentProps.isAddingComment}
              setIsAddingComment={commentProps.setIsAddingComment}
              roundT
            />
          )}
          <RanaMapButtons.BackgroundMap mapId={mapId} roundT={!commentProps} />
          <RanaMapButtons.BuildingsAndTerrain
            show3DBuildings={show3DBuildings}
            setShow3DBuildings={setShow3DBuildings}
          />
          {!hideExport && <RanaMapButtons.MapExport fullscreen={fullscreen} mapId={mapId} />}
          <RanaMapButtons.ThreeD isThreeD={isThreeD} setThreeD={setThreeD} roundB />
        </ControlsTopRight>
      )}

      <ControlsBottomRight>
        <RanaMapButtons.ZoomIn zoomIn={zoomIn} roundT />
        <RanaMapButtons.ZoomOut zoomOut={zoomOut} />
        {showSmallControls && <RanaMapButtons.Compass mapId={mapId} toFlat={toFlat} />}
        <RanaMapButtons.Fullscreen fullscreen={fullscreen} setFullscreen={setFullscreen} roundB />
        <RanaMapButtons.Attribution />
      </ControlsBottomRight>

      {/* Map */}
      <RanaMapSelf {...props} {...hookReturn} />
    </Box>
  );
}

const RanaMapExport = Object.assign(RanaMap, RanaMapLayers, ExportRanaMapControls, {
  MapButton: RanaMapButtons.MapButton,
  MapScale: () => RanaMapScale({ right: 16 }),
});

// This is not a real default export, the elements of the object function as named exports.
// eslint-disable-next-line import/no-default-export
export default RanaMapExport;
